import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { pwaApi, supported, registration, enable, disable, owner } from '@/lib/pwaPush';
import { useSondage } from '@/lib/sondage';

const RETURN = 'huilerie.notification.return';
const validReturn = value => /^\/notifications\/[0-9a-f]{64}$/.test(value || '');
const box = 'rounded-lg border bg-white p-4 space-y-3';
const button = 'rounded border px-4 py-2 disabled:opacity-50';

export function PwaAccess() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (loading) return;
    const target = sessionStorage.getItem(RETURN);
    if (user && validReturn(target)) {
      sessionStorage.removeItem(RETURN); navigate(target, { replace: true });
    }
    // Ne demande jamais la permission. Met seulement à jour l'identité locale
    // si le worker existe déjà (session expirée ou changement de compte).
    if ('serviceWorker' in navigator) navigator.serviceWorker.getRegistration('/').then(reg => {
      if (reg?.active?.scriptURL.endsWith('/service-worker.js')) return owner(user?.role === 'Administrateur' ? user.email : null);
    }).catch(() => {});
  }, [user, loading, navigate]);
  if (user?.role !== 'Administrateur') return null;
  return <Link to="/admin/notifications" aria-label="Notifications administrateur"
    className="fixed bottom-4 right-4 z-40 rounded-full bg-green-900 px-4 py-2 text-sm text-white shadow-lg">Notifications</Link>;
}

export default function Notifications() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [health, setHealth] = useState(null);
  const [list, setList] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [install, setInstall] = useState(null);
  const refresh = useCallback(async () => {
    try {
      const [c, h, l] = await Promise.all([pwaApi('/config'), pwaApi('/health'), pwaApi('/events')]);
      setConfig(c); setHealth(h); setList(l); setError('');
      return [JSON.stringify(c ?? null), Array.isArray(l) ? l.length : 0, Array.isArray(l) ? l[0]?.id ?? null : null];
    } catch (e) { setError(e.message); }
  }, []);
  useSondage(refresh, 15000);
  useEffect(() => {
    refresh();
    const handler = e => { e.preventDefault(); setInstall(e); };
    window.addEventListener('beforeinstallprompt', handler);
    if (supported()) registration().catch(e => setError(e.message));
    return () => { window.removeEventListener('beforeinstallprompt', handler); };
  }, [refresh]);
  async function act(fn, text) {
    setBusy(true); setError(''); setMessage('');
    try { await fn(); setMessage(text); await refresh(); } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return <main className="mx-auto max-w-3xl space-y-4 p-4 pb-24">
    <h1 className="text-2xl font-semibold">Notifications de l’huilerie</h1>
    <p>Réceptions, encaissements clients et décaissements fournisseurs confirmés. Aucun montant n’est affiché sur l’écran verrouillé.</p>
    {error && <p role="alert" className="rounded bg-red-50 p-3 text-red-800">{error}</p>}
    {message && <p role="status" className="rounded bg-green-50 p-3">{message}</p>}
    <section className={box}>
      <h2 className="font-semibold">Ce téléphone</h2>
      <p>Pour installer l’application : menu Chrome ⋮ → Installer l’application ou Ajouter à l’écran d’accueil. L’accès à Internet reste nécessaire.</p>
      {install && <button className={button} onClick={async () => { await install.prompt(); setInstall(null); }}>Installer Huilerie OS</button>}
      <div className="flex flex-wrap gap-2">
        <button className={button} disabled={busy || !supported() || !config?.enabled || !config?.configured}
          onClick={() => act(() => enable(user.email, config), 'Abonnement enregistré pour ce téléphone.')}>Activer les notifications</button>
        <button className={button} disabled={busy || !supported()}
          onClick={() => act(disable, 'Notifications désactivées sur ce téléphone.')}>Désactiver</button>
      </div>
      {!supported() && <p>Utilisez un navigateur Android compatible en HTTPS.</p>}
      {config && (!config.enabled || !config.configured) && <p>Le serveur doit être configuré et activé avant l’abonnement.</p>}
    </section>
    <section className={box}>
      <h2 className="font-semibold">État réel du service</h2>
      <p>Collecte : {health?.collector?.state || 'non vérifiée'} · Envoi : {health?.sender?.state || 'non vérifié'}</p>
      <p>En attente : {health?.pending ?? '—'} · Échecs : {health?.failed ?? '—'}</p>
      {health?.collector?.error && <p>Diagnostic collecte : {health.collector.error}. Contactez l’administrateur technique ; ne réinitialisez pas la file.</p>}
      <button className={button} disabled={busy} onClick={() => act(() => pwaApi('/retry-failed', { method: 'POST' }), 'Reprise des envois échoués demandée.')}>Réessayer les envois échoués</button>
      <button className={button} disabled={busy} onClick={() => act(() => pwaApi('/collector/resume', { method: 'POST' }), 'Reprise de la collecte demandée sans effacer son historique.')}>Reprendre la collecte après réparation</button>
      <p className="text-sm">Une alerte peut arriver en retard. Les opérations restent consultables dans l’ERP même si le téléphone ne reçoit pas de notification.</p>
    </section>
    <section className={box}>
      <h2 className="font-semibold">Les 50 derniers événements</h2>
      {!list.length && <p>Aucun événement depuis l’activation de la collecte.</p>}
      <ul className="divide-y">{list.map(item => <li key={item.id} className="py-3">
        <Link className="underline" to={`/notifications/${item.id}`}>{item.title} — {item.reference}</Link>
        <p className="text-sm">{new Date(item.date).toLocaleString('fr-TN')}</p>
      </li>)}</ul>
    </section>
  </main>;
}

export function NotificationDetail() {
  const { user, loading } = useAuth();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let live = true; setData(null); setError('');
    if (!loading && !user && /^[0-9a-f]{64}$/.test(eventId || '')) sessionStorage.setItem(RETURN, `/notifications/${eventId}`);
    if (user?.role === 'Administrateur') pwaApi(`/events/${encodeURIComponent(eventId)}`).then(x => { if (live) setData(x); }).catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [user, loading, eventId]);
  if (loading) return <p className="p-6">Vérification de la session…</p>;
  if (!user) return <main className="p-6"><h1>Connexion requise</h1><Link className="underline" to="/login">Se connecter pour consulter l’opération</Link></main>;
  if (user.role !== 'Administrateur') return <p role="alert" className="p-6">Accès réservé à l’Administrateur.</p>;
  const labels = { id: 'Référence', date: 'Date', tiersId: 'Code interne du tiers', piece: 'Pièce', montant: 'Montant (TND)', statut: 'Statut', beneficiaire: 'Bénéficiaire', libelle: 'Libellé', compteId: 'Compte', parcours: 'Parcours', poidsBrut: 'Poids brut (kg)', tare: 'Tare (kg)', poidsNet: 'Poids net (kg)', mode: 'Mode' };
  const shown = data?.current || data?.confirmed;
  return <main className="mx-auto max-w-3xl space-y-4 p-6 pb-24">
    <Link className="underline" to="/admin/notifications">← Notifications</Link>
    {error && <p role="alert">{error}</p>}
    {!error && !data && <p>Chargement du bon ou du mouvement…</p>}
    {data && <><h1 className="text-2xl font-semibold">{data.title} — {data.reference}</h1>
      <p>Événement confirmé le {new Date(data.committed_at).toLocaleString('fr-TN')}.</p>
      <p>{data.deleted ? 'Opération supprimée depuis. Informations historiques de la confirmation :' : 'État actuel de l’opération, relu sur le serveur :'}</p>
      <dl className={box}>{Object.entries(labels).filter(([key]) => shown?.[key] !== undefined && shown[key] !== null).map(([key, label]) => <div key={key} className="grid grid-cols-2 gap-4"><dt>{label}</dt><dd>{key === 'montant' ? Number(shown[key]).toLocaleString('fr-TN', {minimumFractionDigits: 3, maximumFractionDigits: 3}) : String(shown[key])}</dd></div>)}</dl>
      <Link className="underline" to={data.module}>Ouvrir le module pour agir sur cette opération</Link>
    </>}
  </main>;
}
